import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import OrganizationApi from "../../../../api/organization-api";
import "./settings-page.scss";

const SettingsPage = () => {
  const [isFeatureEnabled, setIsFeatureEnabled] = useState(false);
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [selectedDays, setSelectedDays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  // AI Integration states
  const [aiProvider, setAiProvider] = useState("gemini");
  const [aiApiKey, setAiApiKey] = useState("");
  const [aiPrompt, setAiPrompt] = useState("");
  const [showTooltip, setShowTooltip] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);

  // Tab state
  const [activeTab, setActiveTab] = useState("whatsapp-config");

  // Confirmation modal states
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [pendingChanges, setPendingChanges] = useState(null);
  const [modalType, setModalType] = useState(""); // "ai" or "office-hours"

  // WhatsApp Sender Details states
  const [whatsappSenderDetails, setWhatsappSenderDetails] = useState(null);
  const [whatsappLoading, setWhatsappLoading] = useState(false);
  const [whatsappError, setWhatsappError] = useState(null);

  // Store existing secrets to preserve them
  const [existingSecrets, setExistingSecrets] = useState({});

  // Get organization ID from Redux store
  const organizationId = useSelector(
    (state) => state?.userStore?.userInfo?._organizations?.[0] || ""
  );

  useEffect(() => {
    const fetchOrganizationSettings = async () => {
      setLoading(true);
      try {
        if (!organizationId) return;

        const response = await OrganizationApi.private.fetchOrganization(organizationId);
        if (response && response.data.data) {
          const { communicationWindowLimits, secrets } = response.data.data;
          
          // Set office hours data
          if (communicationWindowLimits && communicationWindowLimits.length > 0) {
            const limit = communicationWindowLimits[0];
            setStartTime(limit.startTime);
            setEndTime(limit.endTime);
            setSelectedDays(limit.days);
            setIsFeatureEnabled(true);
          }

          // Set AI integration data
          if (secrets) {
            // Store all existing secrets to preserve them
            setExistingSecrets(secrets);
            
            if (secrets.geminiApiKey) {
              setAiProvider("gemini");
              setAiApiKey(secrets.geminiApiKey);
              // Strip the fixed output format from existing prompt for display
              const fixedOutputFormat = '[Output only in a valid JSON object with the key "result" and a boolean value ({result: true/false})]';
              const existingPrompt = secrets.geminiPrompt || "";
              const userPrompt = existingPrompt
                .replace(`. ${fixedOutputFormat}`, '')
                .replace(` ${fixedOutputFormat}`, '')
                .replace(fixedOutputFormat, '')
                .trim();
              setAiPrompt(userPrompt);
            } else if (secrets.openaiApiKey) {
              setAiProvider("openai");
              setAiApiKey(secrets.openaiApiKey);
              const fixedOutputFormat = '[Output only in a valid JSON object with the key "result" and a boolean value ({result: true/false})]';
              const existingPrompt = secrets.openaiPrompt || "";
              const userPrompt = existingPrompt
                .replace(`. ${fixedOutputFormat}`, '')
                .replace(` ${fixedOutputFormat}`, '')
                .replace(fixedOutputFormat, '')
                .trim();
              setAiPrompt(userPrompt);
            } else if (secrets.claudeApiKey) {
              setAiProvider("claude");
              setAiApiKey(secrets.claudeApiKey);
              const fixedOutputFormat = '[Output only in a valid JSON object with the key "result" and a boolean value ({result: true/false})]';
              const existingPrompt = secrets.claudePrompt || "";
              const userPrompt = existingPrompt
                .replace(`. ${fixedOutputFormat}`, '')
                .replace(` ${fixedOutputFormat}`, '')
                .replace(fixedOutputFormat, '')
                .trim();
              setAiPrompt(userPrompt);
            }
          }
        }
      } catch (error) {
        console.error("Error fetching organization settings:", error);
      } finally {
        setLoading(false);
      }
    };

    const fetchWhatsappSenderDetails = async () => {
      if (!organizationId) return;
      
      setWhatsappLoading(true);
      setWhatsappError(null);
      try {
        const response = await OrganizationApi.private.getWhatsappSenderDetails(organizationId);
        if (response && response.data && response.data.data) {
          setWhatsappSenderDetails(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching WhatsApp sender details:", error);
        setWhatsappError("Failed to load WhatsApp sender details");
      } finally {
        setWhatsappLoading(false);
      }
    };

    fetchOrganizationSettings();
    fetchWhatsappSenderDetails();
  }, [organizationId]);

  const weekdays = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];

  const aiProviders = [
    { value: "gemini", label: "Google Gemini" },
    { value: "openai", label: "OpenAI GPT" },
    { value: "claude", label: "Anthropic Claude" },
  ];

  const formatTime = (time) => {
    const [hours, minutes] = time.split(":");
    return `${hours}:${minutes}:00`;
  };

  const handleDayToggle = (day) => {
    if (selectedDays.includes(day)) {
      setSelectedDays(selectedDays.filter((d) => d !== day));
    } else {
      setSelectedDays([...selectedDays, day]);
    }
  };

  const handleSubmit = async () => {
    // Determine which modal to show based on active tab
    if (activeTab === "ai-integration") {
      const fixedOutputFormat = '[Output only in a valid JSON object with the key "result" and a boolean value ({result: true/false})]';
      const fullPrompt = aiPrompt ? `${aiPrompt.trim()}${aiPrompt.trim().endsWith('.') ? ' ' : '. '}${fixedOutputFormat}` : '';
      
      setPendingChanges({
        type: "ai",
        apiKey: aiApiKey,
        prompt: aiPrompt,
        provider: aiProvider,
        fullPrompt: fullPrompt
      });
      setModalType("ai");
      setShowConfirmModal(true);
    } else if (activeTab === "office-hours") {
      setPendingChanges({
        type: "office-hours",
        isEnabled: isFeatureEnabled,
        startTime: startTime,
        endTime: endTime,
        selectedDays: selectedDays
      });
      setModalType("office-hours");
      setShowConfirmModal(true);
    }
  };

  const handleConfirmSave = async () => {
    if (confirmText.toLowerCase() !== "confirm") {
      return;
    }
    
    setLoading(true);
    setShowConfirmModal(false);
    setConfirmText("");
    
    try {
      if (!organizationId) {
        console.error("Organization ID not found.");
        return;
      }

      let payload = {};

      if (pendingChanges.type === "ai") {
        // Start with existing secrets to preserve all current values
        const updatedSecrets = { ...existingSecrets };

        // Update only the specific AI provider credentials
        if (aiProvider === "gemini" && aiApiKey) {
          updatedSecrets.geminiApiKey = aiApiKey;
          updatedSecrets.geminiPrompt = pendingChanges.fullPrompt;
          // Clear other AI provider credentials if they exist
          delete updatedSecrets.openaiApiKey;
          delete updatedSecrets.openaiPrompt;
          delete updatedSecrets.claudeApiKey;
          delete updatedSecrets.claudePrompt;
        } else if (aiProvider === "openai" && aiApiKey) {
          updatedSecrets.openaiApiKey = aiApiKey;
          updatedSecrets.openaiPrompt = pendingChanges.fullPrompt;
          // Clear other AI provider credentials if they exist
          delete updatedSecrets.geminiApiKey;
          delete updatedSecrets.geminiPrompt;
          delete updatedSecrets.claudeApiKey;
          delete updatedSecrets.claudePrompt;
        } else if (aiProvider === "claude" && aiApiKey) {
          updatedSecrets.claudeApiKey = aiApiKey;
          updatedSecrets.claudePrompt = pendingChanges.fullPrompt;
          // Clear other AI provider credentials if they exist
          delete updatedSecrets.geminiApiKey;
          delete updatedSecrets.geminiPrompt;
          delete updatedSecrets.openaiApiKey;
          delete updatedSecrets.openaiPrompt;
        }

        payload = { secrets: updatedSecrets };
      } else if (pendingChanges.type === "office-hours") {
        // Office hours payload
        payload = pendingChanges.isEnabled
          ? {
              communicationWindowLimits: [
                {
                  startTime: formatTime(pendingChanges.startTime),
                  endTime: formatTime(pendingChanges.endTime),
                  days: pendingChanges.selectedDays,
                  timezone: "Europe/London", // Replace with dynamic timezone if needed
                },
              ],
            }
          : { communicationWindowLimits: [] };
      }

      await OrganizationApi.private.updateOrganization(organizationId, payload);
      console.log("Settings updated successfully:", payload);
      setSuccessMessage("Settings updated successfully.");
      setTimeout(() => setSuccessMessage(""), 5000);
      setPendingChanges(null);
    } catch (error) {
      console.error("Error updating settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowConfirmModal(false);
    setConfirmText("");
    setPendingChanges(null);
    setModalType("");
  };

  return (
    <div className="featuresSettingsPageWrapper">
      <h1 className="m-0">Features Settings</h1>

      <div className="formWrapper">
        {successMessage && <div className="alert alert-success">{successMessage}</div>}

        {/* Tab Navigation */}
        <div className="settings-tabs">
          <ul className="nav nav-tabs" role="tablist">
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === "whatsapp-config" ? "active" : ""}`}
                onClick={() => setActiveTab("whatsapp-config")}
                type="button"
                role="tab"
              >
                <span className="tab-icon me-2">📱</span>
                WhatsApp Configuration
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === "office-hours" ? "active" : ""}`}
                onClick={() => setActiveTab("office-hours")}
                type="button"
                role="tab"
              >
                <span className="tab-icon me-2">🕐</span>
                Office Hours
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === "ai-integration" ? "active" : ""}`}
                onClick={() => setActiveTab("ai-integration")}
                type="button"
                role="tab"
              >
                <span className="tab-icon me-2">🤖</span>
                AI Integration
              </button>
            </li>
          </ul>
        </div>

        {/* Tab Content */}
        <div className="tab-content">
          
          {/* WhatsApp Configuration Tab */}
          {activeTab === "whatsapp-config" && (
            <div className="tab-pane fade show active">
              <div className="card">
                <div className="card-header p-0 mb-4">
                  <h3>WhatsApp Sender Configuration</h3>
                  <p>View your WhatsApp sender details and status</p>
                </div>
                
                <div className="px-3">
                  {whatsappLoading && (
                    <div className="text-center py-4">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="mt-2 text-muted">Loading WhatsApp sender details...</p>
                    </div>
                  )}
                  
                  {whatsappError && (
                    <div className="alert alert-danger" role="alert">
                      <strong>Error:</strong> {whatsappError}
                      <button 
                        className="btn btn-outline-danger btn-sm ms-3"
                        onClick={() => {
                          if (organizationId) {
                            setWhatsappLoading(true);
                            setWhatsappError(null);
                            OrganizationApi.private.getWhatsappSenderDetails(organizationId)
                              .then(response => {
                                if (response && response.data && response.data.data) {
                                  setWhatsappSenderDetails(response.data.data);
                                }
                              })
                              .catch(error => {
                                console.error("Error fetching WhatsApp sender details:", error);
                                setWhatsappError("Failed to load WhatsApp sender details");
                              })
                              .finally(() => setWhatsappLoading(false));
                          }
                        }}
                      >
                        Retry
                      </button>
                    </div>
                  )}
                  
                  {whatsappSenderDetails && !whatsappLoading && (
                    <div className="whatsapp-sender-details">
                      <div className="row">
                        <div className="col-md-6 mb-4">
                          <div className="card noOutline p-0 card-sub-section">
                            <div className="card-body">
                              <h5 className="card-title d-flex align-items-center">
                                <span className="me-2">📞</span>
                                WhatsApp Number
                              </h5>
                              <p className="card-text fs-5 fw-bold text-primary">
                                {whatsappSenderDetails.senderId?.replace('whatsapp:', '') || 'N/A'}
                              </p>
                            </div>
                          </div>
                        </div>
                        
                        <div className="col-md-6 mb-4">
                          <div className="card noOutline p-0 card-sub-section">
                            <div className="card-body">
                              <h5 className="card-title d-flex align-items-center">
                                <span className="me-2">📊</span>
                                Status
                              </h5>
                              <span className={`badge fs-6 ${
                                whatsappSenderDetails.status === 'ONLINE' 
                                  ? 'bg-success' 
                                  : whatsappSenderDetails.status === 'OFFLINE'
                                  ? 'bg-danger'
                                  : 'bg-warning'
                              }`}>
                                {whatsappSenderDetails.status || 'Unknown'}
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="col-md-6 mb-4">
                          <div className="card noOutline p-0 card-sub-section">
                            <div className="card-body">
                              <h5 className="card-title d-flex align-items-center">
                                <span className="me-2">⭐</span>
                                Quality Rating
                              </h5>
                              <span className={`badge fs-6 ${
                                whatsappSenderDetails.properties?.quality_rating === 'HIGH'
                                  ? 'bg-success'
                                  : whatsappSenderDetails.properties?.quality_rating === 'MEDIUM'
                                  ? 'bg-warning'
                                  : whatsappSenderDetails.properties?.quality_rating === 'LOW'
                                  ? 'bg-danger'
                                  : 'bg-secondary'
                              }`}>
                                {whatsappSenderDetails.properties?.quality_rating || 'Unknown'}
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="col-md-6 mb-4">
                          <div className="card noOutline p-0 card-sub-section">
                            <div className="card-body">
                              <h5 className="card-title d-flex align-items-center">
                                <span className="me-2">📈</span>
                                Messaging Limit
                              </h5>
                              <p className="card-text fs-6 fw-semibold">
                                {whatsappSenderDetails.properties?.messaging_limit || 'Unavailable'}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {whatsappSenderDetails.offlineReasons && whatsappSenderDetails.offlineReasons.length > 0 && (
                        <div className="mt-4">
                          <div className="alert alert-warning">
                            <h6 className="alert-heading">Offline Reasons:</h6>
                            <ul className="mb-0">
                              {whatsappSenderDetails.offlineReasons.map((reason, index) => (
                                <li key={index}>{reason}</li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      )}
                      
                      <div className="mt-4 text-center">
                        <button 
                          className="btn btn-outline-primary"
                          onClick={() => {
                            if (organizationId) {
                              setWhatsappLoading(true);
                              setWhatsappError(null);
                              OrganizationApi.private.getWhatsappSenderDetails(organizationId)
                                .then(response => {
                                  if (response && response.data && response.data.data) {
                                    setWhatsappSenderDetails(response.data.data);
                                  }
                                })
                                .catch(error => {
                                  console.error("Error fetching WhatsApp sender details:", error);
                                  setWhatsappError("Failed to load WhatsApp sender details");
                                })
                                .finally(() => setWhatsappLoading(false));
                            }
                          }}
                          disabled={whatsappLoading}
                        >
                          <span className="me-2">🔄</span>
                          Refresh Details
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {/* Office Hours Tab */}
          {activeTab === "office-hours" && (
            <div className="tab-pane fade show active">
              <div className="card">
                <div className="card-header p-0 mb-4">
                  <h3>Configure Office Hours</h3>
                  <p>Set when your organization is available for communication</p>
                </div>
                
                <div className="px-3">
                  <div className="form-check mb-3">
                    <input
                      type="checkbox"
                      id="enableFeature"
                      className="form-check-input"
                      checked={isFeatureEnabled}
                      onChange={(e) => setIsFeatureEnabled(e.target.checked)}
                    />
                    <label htmlFor="enableFeature" className="form-check-label">
                      Enable Office hours
                    </label>
                  </div>

                  {isFeatureEnabled && (
                    <>
                      <div className="row">
                        <div className="col-md-6">
                          <div className="form-group">
                            <label htmlFor="startTime">Office hours Start Time</label>
                            <input
                              type="time"
                              id="startTime"
                              className="form-control"
                              value={startTime}
                              onChange={(e) => setStartTime(e.target.value)}
                            />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-group">
                            <label htmlFor="endTime">Office hours End Time</label>
                            <input
                              type="time"
                              id="endTime"
                              className="form-control"
                              value={endTime}
                              onChange={(e) => setEndTime(e.target.value)}
                            />
                          </div>
                        </div>
                      </div>

                      <div className="form-group">
                        <label>Select Days</label>
                        <div className="weekdays">
                          {weekdays.map((day) => (
                            <div key={day} className="form-check">
                              <input
                                type="checkbox"
                                id={day}
                                className="form-check-input"
                                checked={selectedDays.includes(day.toLocaleLowerCase())}
                                onChange={() => handleDayToggle(day.toLocaleLowerCase())}
                              />
                              <label htmlFor={day} className="form-check-label">
                                {day}
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* AI Integration Tab */}
          {activeTab === "ai-integration" && (
            <div className="tab-pane fade show active">
              <div className="card">
                <div className="card-header p-0 mb-0">
                  <h3>AI-Powered Content Moderation</h3>
                  <p>Configure AI to automatically analyze and moderate incoming messages</p>
                </div>
                
                <div className="">
                  <div className="card noOutline p-0">
                    <div className="card-body px-3">
                      
                      {/* Provider Selection */}
                      <div className="card noOutline p-0 card-sub-section provider-section mb-4">
                        <label className="card-subheader mb-0">
                          <h4 className="mb-0"> AI Provider </h4>
                        </label>
                        <div className="provider-grid">
                          {aiProviders.map((provider) => (
                            <div 
                              key={provider.value} 
                              className={`provider-card ${aiProvider === provider.value ? 'active' : ''}`}
                              onClick={() => setAiProvider(provider.value)}
                            >
                              <div className="provider-icon-large">
                                {provider.value === 'gemini' && '🧠'}
                                {provider.value === 'openai' && '💡'}
                                {provider.value === 'claude' && '🎯'}
                              </div>
                              <div className="provider-name">{provider.label}</div>
                              
                                {aiProvider === provider.value && <div className="provider-check"><span>✓</span></div>}
                              
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* API Key Section */}
                      <div className="card noOutline p-0 card-sub-section api-key-section mb-4">
                        <label className="card-subheader">
                          <h4 className="mb-0"> API Key </h4>
                        </label>
                        <div className="input-group">
                          <span className="input-group-text bg-light border-end-0">
                            <span className="text-muted">🔐</span>
                          </span>
                          <input
                            type={showApiKey ? "text" : "password"}
                            id="aiApiKey"
                            className="form-control border-start-0 border-end-0 ps-0"
                            value={aiApiKey}
                            onChange={(e) => setAiApiKey(e.target.value)}
                            placeholder={`Enter your ${aiProviders.find(p => p.value === aiProvider)?.label} API key`}
                          />
                          <button
                            type="button"
                            className="btn btn-outline-secondary border-start-0"
                            onClick={() => setShowApiKey(!showApiKey)}
                            style={{ borderLeft: 'none' }}
                          >
                            {showApiKey ? '🙈' : '👁️'}
                          </button>
                        </div>
                        <small className="form-text text-muted mt-1">
                          Your API key is encrypted and stored securely
                        </small>
                      </div>

                      {/* Prompt Section */}
                      <div className="card noOutline p-0 card-sub-section prompt-section">
                        <div className="d-flex align-items-center gap-3 my-4">
                          <label className="card-subheader mb-0">
                            <h4 className="m-0">AI Prompt</h4>
                          </label>
                          
                          <div className="btnWrapper">
                            <button 
                              type="button"
                              className="d-inline-block btn btn-outline-info btn-sm"
                              onClick={() => setShowTooltip(!showTooltip)}
                            >
                              📋 Instructions
                            </button>
                          </div>
                        </div>
                        
                        {showTooltip && (
                          <div className="instructions-accordion">
                            <div className="accordion-content">
                              <div className="instruction-section">
                                <h6 className="text-primary">📝 Example Prompt:</h6>
                                <div className="example-text">
                                  <em>It's an incoming message from one of our contacts. Please analyze the message to determine if the sender is explicitly asking for or sharing personal contact information (such as email addresses, phone numbers, physical addresses) or using abusive language.</em>
                                </div>
                              </div>
                              
                              <div className="instruction-section">
                                <h6 className="text-info">⚙️ Auto-Generated Output Format:</h6>
                                <div className="format-text">
                                  <code>Output only in a valid JSON object with the key "result" and a boolean value (result: true/false)</code>
                                </div>
                                <small className="text-muted d-block mt-2">
                                  This format will be automatically appended to your prompt.
                                </small>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        <textarea
                          id="aiPrompt"
                          className="form-control prompt-textarea"
                          rows="4"
                          value={aiPrompt}
                          onChange={(e) => setAiPrompt(e.target.value)}
                          placeholder="Enter the prompt that will be used for AI analysis of incoming messages..."
                        />
                        <small className="form-text text-muted mb-3">
                          Define how the AI should analyze incoming messages for content moderation
                        </small>
                        
                        {/* Simplified Output Format Notice */}
                        <div className="output-format-notice">
                          <small className="text-muted">
                            <span className="format-icon me-1">⚙️</span>
                            <em>Note: A standard JSON output format will be automatically appended to ensure consistent AI responses.</em>
                          </small>
                        </div>
                      </div>
                      
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

        </div>

        {/* Only show save button for tabs that have editable content */}
        {activeTab !== "whatsapp-config" && (
          <div className="buttons chat-new-start mt-0 mb-5">
            <button className="bouncy-button" onClick={handleSubmit} disabled={loading}>
              <span>{ loading ? "Saving" : "Save" }</span>
            </button>
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="custom-modal-overlay show" role="dialog" aria-labelledby="confirmModalTitle" aria-hidden={!showConfirmModal}>
          <div className="custom-modal-content">
            <div className="modal-header">
              <h5 className="modal-title mt-0" id="confirmModalTitle">
                Confirm {modalType === "ai" ? "AI Integration" : "Office Hours"} Changes
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleCloseModal}
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <div className="confirmation-content">
                <p className="confirmation-message mb-4">
                  Please review your {modalType === "ai" ? "AI integration" : "office hours"} settings before saving:
                </p>

                {modalType === "ai" && pendingChanges && (
                  <div className="settings-review">
                    <div className="review-section mb-3">
                      <strong>AI Provider:</strong>
                      <span className="ms-2">{aiProviders.find(p => p.value === pendingChanges.provider)?.label}</span>
                    </div>
                    <div className="review-section mb-3">
                      <strong>API Key:</strong>
                      <span className="ms-2 font-monospace">
                        {pendingChanges.apiKey ? `${pendingChanges.apiKey.substring(0, 8)}${'*'.repeat(Math.max(0, pendingChanges.apiKey.length - 8))}` : 'Not set'}
                      </span>
                    </div>
                    <div className="review-section mb-3">
                      <strong>AI Prompt:</strong>
                      <div className="mt-2 p-3 bg-light border rounded">
                        <small className="text-muted">{pendingChanges.prompt || 'No prompt set'}</small>
                      </div>
                    </div>
                  </div>
                )}

                {modalType === "office-hours" && pendingChanges && (
                  <div className="settings-review">
                    <div className="review-section mb-3">
                      <strong>Office Hours Enabled:</strong>
                      <span className="ms-2">{pendingChanges.isEnabled ? 'Yes' : 'No'}</span>
                    </div>
                    {pendingChanges.isEnabled && (
                      <>
                        <div className="review-section mb-3">
                          <strong>Time Range:</strong>
                          <span className="ms-2">{pendingChanges.startTime} - {pendingChanges.endTime}</span>
                        </div>
                        <div className="review-section mb-3">
                          <strong>Selected Days:</strong>
                          <div className="mt-2">
                            {pendingChanges.selectedDays.length > 0 ? (
                              <span className="ms-2">{pendingChanges.selectedDays.map(day => day.charAt(0).toUpperCase() + day.slice(1)).join(', ')}</span>
                            ) : (
                              <span className="ms-2 text-muted">No days selected</span>
                            )}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}

                <div className="confirmation-input mt-4">
                  <label htmlFor="confirmInput" className="form-label">
                    <strong>Type "Confirm" to proceed with saving these changes:</strong>
                  </label>
                  <input
                    type="text"
                    id="confirmInput"
                    className="form-control"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder="Type 'Confirm' here"
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer justify-content-end">
              <button
                type="button"
                className="btn"
                onClick={handleCloseModal}
                disabled={loading}
              >
                <span>Cancel</span>
              </button>
              <button
                type="button"
                className="bouncy-button"
                onClick={handleConfirmSave}
                disabled={loading || confirmText.toLowerCase() !== "confirm"}
              >
                <span>{loading ? "Saving..." : "Save Changes"}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsPage;


